import React, { useRef, useContext, useState } from "react";
import { useTheme } from "@emotion/react";
import { Animated, useWindowDimensions } from "react-native";
import { shuffle } from "lodash";
import Quiz from "../Quiz";
import DrawerButton from "../../components/DrawerButton";
import MoodSelector from "../../components/MoodSelector";
import QuizCta from "../../components/QuizCta";
import QuizQueue from "../../components/QuizQueue";
import SafetyStrategyItem from "../../components/SafetyStrategyItem";
import SafeModeTooltip from "../../components/SafeModeTooltip";
import { createStackNavigator, RouterType } from "../../utils/routing";
import { QuizOrderContext } from "../../utils/quiz";
import { StrategyOrderContext } from "../../utils/strategy";
import { FurnitureContext } from "../../utils/furniture";
import {
    Container,
    Background,
    Scroll,
    Spacer,
    Content,
    Intro,
    SafetyStrategies,
    SafetyStrategiesHeadline,
    SafetyStrategiesScroll,
    TakenAllQuizzes,
    TakenAllQuizzesIntro,
    ReviewQuizzesButton,
    QuizQueueContainer,
} from "./styles";
import imageUrl from "../../utils/imageUrl";

const Stack = createStackNavigator(RouterType.Home);

const Navigator = () => {
    return (
        <Stack.Navigator
            initialRouteName="Home"
            screenOptions={{
                headerShown: false,
            }}>
            <Stack.Screen name="Home" component={Home} />
            <Stack.Screen name="Quiz" component={Quiz} />
        </Stack.Navigator>
    );
};

const Home = ({ navigation, route }) => {
    const windowWidth = 800;
    const { backgroundPattern }: any = useTheme();
    const quizOrder = useContext(QuizOrderContext);
    const strategyOrder = useContext(StrategyOrderContext);
    const furniture = useContext(FurnitureContext);
    const [isAnimating, setIsAnimating] = useState(false);
    const contentTranslateY = useRef(new Animated.Value(0)).current;

    if (!quizOrder || !strategyOrder) {
        return <Container />;
    }

    const randomStrategies = shuffle(strategyOrder.available).slice(0, 3);

    return (
        <Container nativeID="home-container">
            <Background source={backgroundPattern} />
            <DrawerButton />
            <Scroll showsVerticalScrollIndicator={false}>
                {furniture?.enableMoodSelector && (
                    <MoodSelector
                        contentTranslateY={contentTranslateY}
                        onAnimationStart={() => setIsAnimating(true)}
                        onAnimationStop={() => {
                            setIsAnimating(false);
                        }}
                    />
                )}
                <Content
                    nativeID="home-content-scroll"
                    onStartShouldSetResponderCapture={event => isAnimating}
                    style={{
                        transform: [
                            {
                                translateY: contentTranslateY,
                            },
                        ],
                    }}>
                    {quizOrder.completed.length === 0 && (
                        <Intro type="h2">
                            {furniture.strings.homeLabelLetsBegin}
                        </Intro>
                    )}
                    {!quizOrder.next && (
                        <TakenAllQuizzes>
                            <TakenAllQuizzesIntro type="mono2">
                                {furniture.strings.homeLabelAllQuizzes}
                            </TakenAllQuizzesIntro>
                            <ReviewQuizzesButton
                                title={furniture.ctaReviewYourResults}
                                onPress={() =>
                                    navigation.navigate("MyRelationship")
                                }
                            />
                        </TakenAllQuizzes>
                    )}
                    {quizOrder.next && (
                        <QuizCta
                            quiz={quizOrder.next}
                            onPress={() =>
                                navigation.navigate("Quiz", {
                                    quizId: quizOrder.next._id,
                                    quizOrder,
                                })
                            }
                        />
                    )}

                    {quizOrder.completed.length > 0 && (
                        <SafetyStrategies>
                            <SafetyStrategiesHeadline type="h2">
                                {furniture.strings.homeLabelReviewStrategies}
                            </SafetyStrategiesHeadline>
                            <SafetyStrategiesScroll
                                horizontal={true}
                                showsHorizontalScrollIndicator={false}>
                                {randomStrategies.map(strategy => {
                                    const source = imageUrl(strategy.image);
                                    source.uri = `${source.uri}?w=${windowWidth}&fm=webp`;

                                    return (
                                        <SafetyStrategyItem
                                            key={strategy._id}
                                            imageSource={source}
                                            title={strategy.title}
                                            titleRich={strategy.titleRich}
                                            onPress={() =>
                                                navigation.navigate(
                                                    "Strategy",
                                                    {
                                                        strategyId:
                                                            strategy._id,
                                                        _meta: {
                                                            strategyCategoryId:
                                                                strategy._meta
                                                                    .strategyCategoryId,
                                                        },
                                                    },
                                                )
                                            }
                                        />
                                    );
                                })}
                            </SafetyStrategiesScroll>
                        </SafetyStrategies>
                    )}

                    {quizOrder.completed.length > 0 &&
                        quizOrder.queue?.length > 0 && (
                            <QuizQueueContainer>
                                <QuizQueue
                                    queue={quizOrder.queue}
                                    onPressItem={quizId =>
                                        navigation.navigate("Quiz", {
                                            quizId,
                                            quizOrder,
                                        })
                                    }
                                />
                            </QuizQueueContainer>
                        )}
                    <Spacer
                        style={{
                            height: contentTranslateY as unknown as number,
                        }}
                    />
                </Content>
            </Scroll>
            {quizOrder.completed.length === 0 && <SafeModeTooltip />}
        </Container>
    );
};

export default Navigator;
